import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ViewMode } from "../components/molecules/ViewModeToggle/ViewModeToggle.types";

export interface ConnectorMarketplaceHeaderBridge {
  search: string;
  setSearch: (value: string) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  /** Hide the grid/list toggle in the shell header (default true). */
  showViewModeToggle?: boolean;
}

type ConnectorMarketplaceHeaderContextValue = {
  bridge: ConnectorMarketplaceHeaderBridge | null;
  setBridge: (next: ConnectorMarketplaceHeaderBridge | null) => void;
};

const ConnectorMarketplaceHeaderContext =
  createContext<ConnectorMarketplaceHeaderContextValue | null>(null);

export const ConnectorMarketplaceHeaderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [bridge, setBridgeState] =
    useState<ConnectorMarketplaceHeaderBridge | null>(null);
  const setBridge = useCallback(
    (next: ConnectorMarketplaceHeaderBridge | null) => {
      setBridgeState(next);
    },
    [],
  );
  const value = useMemo(
    () => ({ bridge, setBridge }),
    [bridge, setBridge],
  );
  return (
    <ConnectorMarketplaceHeaderContext.Provider value={value}>
      {children}
    </ConnectorMarketplaceHeaderContext.Provider>
  );
};

export function useConnectorMarketplaceHeaderBridge(): ConnectorMarketplaceHeaderBridge | null {
  const ctx = useContext(ConnectorMarketplaceHeaderContext);
  return ctx?.bridge ?? null;
}

/**
 * Publishes marketplace search + view mode to the shell header while this page is mounted.
 */
export function useRegisterConnectorMarketplaceHeader(
  search: string,
  setSearch: (value: string) => void,
  viewMode: ViewMode,
  setViewMode: (mode: ViewMode) => void,
  showViewModeToggle = true,
): void {
  const ctx = useContext(ConnectorMarketplaceHeaderContext);
  const setBridge = ctx?.setBridge;
  useEffect(() => {
    if (!setBridge) return;
    setBridge({ search, setSearch, viewMode, setViewMode, showViewModeToggle });
    return () => setBridge(null);
  }, [search, setSearch, viewMode, setViewMode, showViewModeToggle, setBridge]);
}
